import { lazy } from "react";
import type {
  Data,
  ContentComponentConfig,
  ContentRootProps,
  ExcursionComponentConfig,
  ExcursionRootProps,
  StickyCardsProps,
} from "@op/puck-types";

const AuthorCard = lazy(
  () => import("../components/puck/AuthorCard/AuthorCard")
);

const Accordion = lazy(() => import("../components/puck/Accordion/Accordion"));

const Paragraph = lazy(() => import("../components/puck/Paragraph/Paragraph"));

const LayoutRow = lazy(() => import("../components/puck/LayoutRow/LayoutRow"));

const Medium = lazy(() => import("../components/puck/Medium/Medium"));

const ExcursionCard = lazy(
  () => import("../components/puck/ExcursionCard/ExcursionCard")
);

const Heading = lazy(() => import("../components/puck/Heading/Heading"));

const Grid = lazy(() => import("../components/puck/Grid/Grid"));

const Blockquote = lazy(
  () => import("../components/puck/Blockquote/Blockquote")
);

const Callout = lazy(() => import("../components/puck/Callout/Callout"));

const SectionPreview = lazy(
  () => import("../components/puck/SectionPreview/SectionPreview")
);

const TextCard = lazy(() => import("../components/puck/TextCard/TextCard"));

const VideoEmbed = lazy(
  () => import("../components/puck/VideoEmbed/VideoEmbed")
);

export interface ComponentRendererProps {
  node:
    | Data<ContentComponentConfig, ContentRootProps>["content"][number]
    | Data<ExcursionComponentConfig, ExcursionRootProps>["content"][number];
}

const StickyCards = lazy(
  () => import("../components/puck/StickyCards/StickyCards")
);

const TabCard = lazy(() => import("../components/puck/TabCard/TabCard"));

export const ComponentRenderer = ({ node }: ComponentRendererProps) => {
  switch (node.type) {
    case "Accordion":
      return <Accordion {...node.props} />;
    case "AuthorCard":
      return <AuthorCard {...node.props} />;
    case "Blockquote":
      return <Blockquote {...node.props} />;
    case "Callout":
      return <Callout {...node.props} />;
    case "ExcursionCard":
      return <ExcursionCard {...node.props} />;
    case "Grid":
      return (
        <Grid {...node.props}>
          {node.props.slot1?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
          {node.props.slot2?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
          {node.props.slot3?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
          {node.props.slot4?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
        </Grid>
      );
    case "Heading":
      return <Heading {...node.props} />;
    case "LayoutRow":
      return (
        <LayoutRow
          key={node.props.id}
          {...node.props}
          leftChildren={node.props.leftChildren?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
          rightChildren={node.props.rightChildren?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
        >
          {node.props.children?.map((child) => (
            <ComponentRenderer key={child.props.id} node={child} />
          ))}
        </LayoutRow>
      );
    case "Medium":
      return <Medium {...node.props} />;
    case "Paragraph":
      return <Paragraph {...node.props} />;
    case "SectionPreview":
      return <SectionPreview {...node.props} />;
    case "StickyCards":
      return <StickyCards {...(node.props as StickyCardsProps)} />;
    case "TabCard":
      return <TabCard {...node.props} />;
    case "TextCard":
      return <TextCard {...node.props} />;
    case "VideoEmbed":
      return <VideoEmbed {...node.props} />;
    default:
      return null;
  }
};
